import { CMS_MEDIA_BASE_URL } from './client'

export const toMediaUrl = (url?: string | null) => {
  if (!url) return ''
  if (url.startsWith('http://') || url.startsWith('https://')) return url
  return `${CMS_MEDIA_BASE_URL}${url}`
}

/**
 * Strapi v4: fields under `attributes`. Strapi v5: fields flat on the entry.
 * Merge both so component arrays are never missed when `attributes` is `{}` but
 * `our_workflow` / `Services` live on the parent object.
 */
export function getStrapiFields(entry: unknown): Record<string, unknown> {
  if (!entry || typeof entry !== 'object') return {}
  const e = entry as Record<string, unknown>
  const attrs = e.attributes
  if (attrs && typeof attrs === 'object' && !Array.isArray(attrs)) {
    return { ...e, ...(attrs as Record<string, unknown>) }
  }
  return e
}

/** Strapi repeatable components / relations: array, or `{ data: [...] }`. */
export function asStrapiComponentList(field: unknown): unknown[] {
  if (!field) return []
  if (Array.isArray(field)) return field
  if (typeof field === 'object') {
    const o = field as Record<string, unknown>
    if (Array.isArray(o.data)) return o.data
  }
  return []
}

/** First matching non-empty component list (handles alternate API field casing). */
export function pickStrapiComponentList(
  fields: Record<string, unknown>,
  ...keys: string[]
): unknown[] {
  for (const key of keys) {
    const list = asStrapiComponentList(fields[key])
    if (list.length) return list
  }
  return []
}

/**
 * Normalize Strapi media to absolute URL.
 * v5: `{ url: '/uploads/...' }` or array of those
 * v4: `{ data: { attributes: { url } } }` or `data: [{ attributes: { url } }]`
 */
/** All media URLs from a Strapi field (single asset or array). */
export function extractMediaUrls(media: unknown): string[] {
  if (!media) return []
  if (typeof media === 'string') {
    const url = toMediaUrl(media)
    return url ? [url] : []
  }
  if (Array.isArray(media)) {
    return media.map((item) => extractMediaUrl(item)).filter(Boolean)
  }
  const single = extractMediaUrl(media)
  return single ? [single] : []
}

export function extractMediaUrl(media: unknown): string {
  if (!media) return ''
  if (typeof media === 'string') return toMediaUrl(media)
  if (Array.isArray(media)) {
    return media.length ? extractMediaUrl(media[0]) : ''
  }
  if (typeof media !== 'object') return ''
  const m = media as Record<string, unknown>
  if (typeof m.url === 'string') return toMediaUrl(m.url)

  const data = m.data
  if (Array.isArray(data) && data.length) {
    const first = data[0]
    if (first && typeof first === 'object') {
      const inner = first as Record<string, unknown>
      const attrs = inner.attributes as Record<string, unknown> | undefined
      if (attrs?.url && typeof attrs.url === 'string') return toMediaUrl(attrs.url as string)
      if (inner.url && typeof inner.url === 'string') return toMediaUrl(inner.url as string)
    }
  }
  if (data && typeof data === 'object' && !Array.isArray(data)) {
    const d = data as Record<string, unknown>
    const attrs = d.attributes as Record<string, unknown> | undefined
    if (attrs?.url && typeof attrs.url === 'string') return toMediaUrl(attrs.url as string)
    if (d.url && typeof d.url === 'string') return toMediaUrl(d.url as string)
  }
  return ''
}

/** True in `npm run dev`, or when VITE_CMS_DEBUG=true (e.g. production preview). */
export const isCmsDebugEnabled = () =>
  import.meta.env.DEV || import.meta.env.VITE_CMS_DEBUG === 'true'

export const cmsDebug = (...args: unknown[]) => {
  if (!isCmsDebugEnabled()) return
  console.info('[DigiTrek CMS]', ...args)
}

/**
 * Remove Strapi entry id only when pasted at the very start of a field
 * (e.g. `115hello@digitrek.ae`). Does not alter numbers inside addresses
 * (e.g. `Dubai, United Arab Emirates,115street` stays as in CMS).
 */
export function stripStrapiIdPrefix(text: string): string {
  const s = text.trim()
  if (!s || /^\d+$/.test(s)) return ''
  return s
    .replace(/^(\d{1,10})(?=[a-zA-Z@+#(/])/u, '')
    .replace(/^(\d{1,10})\s+/u, '')
    .trim()
}

/**
 * Normalize Strapi text fields (plain string, blocks, or nested components).
 * Ignores bare numeric ids so entry/component ids are not shown in the UI.
 */
export function plainCmsText(value: unknown): string {
  if (value == null) return ''
  if (typeof value === 'string') {
    return stripStrapiIdPrefix(value)
  }
  if (typeof value === 'number' || typeof value === 'boolean') return ''
  if (Array.isArray(value)) {
    return value
      .map((item) => plainCmsText(item))
      .filter(Boolean)
      .join(' ')
      .trim()
  }
  if (typeof value === 'object') {
    const o = value as Record<string, unknown>
    if (typeof o.text === 'string') return plainCmsText(o.text)
    if (Array.isArray(o.children)) {
      return o.children
        .map((child) => plainCmsText(child))
        .filter(Boolean)
        .join('')
        .trim()
    }
    const attrs = o.attributes
    if (attrs && typeof attrs === 'object' && !Array.isArray(attrs)) {
      const fromAttrs = plainCmsText(attrs)
      if (fromAttrs) return fromAttrs
    }
    if (o.data != null) {
      const fromData = plainCmsText(o.data)
      if (fromData) return fromData
    }
    for (const key of [
      'value',
      'Value',
      'Title',
      'title',
      'Description',
      'description',
      'Email',
      'email',
      'Phone',
      'phone',
      'Address',
      'address',
    ]) {
      const nested = plainCmsText(o[key])
      if (nested) return nested
    }
    return ''
  }
  return stripStrapiIdPrefix(String(value))
}

/** First non-empty normalized CMS string from field name variants. */
export function pickCmsText(...values: unknown[]): string {
  for (const v of values) {
    const s = plainCmsText(v)
    if (s) return s
  }
  return ''
}

/** Preserves paragraph breaks when Strapi returns a blocks array. */
export function pickCmsMultilineText(...values: unknown[]): string {
  for (const v of values) {
    if (Array.isArray(v)) {
      const parts = v.map((part) => plainCmsText(part)).filter(Boolean)
      if (parts.length) return parts.join('\n\n')
    }
    const s = plainCmsText(v)
    if (s) return s.replace(/\n{3,}/g, '\n\n').trim()
  }
  return ''
}

/** CMS string when present; static fallback only after fetch settles (avoids flash on refresh). */
export function cmsTextOrFallback(
  value: string | undefined | null,
  fallback: string,
  cmsReady: boolean
): string {
  const trimmed = typeof value === 'string' ? value.trim() : ''
  if (trimmed) return trimmed
  return cmsReady ? fallback : ''
}

/** CMS list when non-empty; static fallback only after fetch settles. */
export function cmsListOrFallback<T>(
  value: readonly T[] | undefined | null,
  fallback: readonly T[],
  cmsReady: boolean
): readonly T[] {
  if (value?.length) return value
  return cmsReady ? fallback : []
}

/** Contact/footer field: CMS value, else static placeholder only when not waiting on fetch. */
export function cmsContactField(
  value: unknown,
  staticFallback: string,
  cmsReady: boolean
): string {
  const fromCms = pickCmsText(value)
  if (fromCms) return fromCms
  return cmsReady ? staticFallback : ''
}

export function resolveCmsEntryOrder(entry: unknown, fallbackIndex = Number.MAX_SAFE_INTEGER): number {
  const fields = getStrapiFields(entry)
  for (const key of ['Order', 'order', 'DisplayOrder', 'displayOrder', 'Sort', 'sort']) {
    const raw = fields[key]
    if (typeof raw === 'number' && Number.isFinite(raw)) return raw
    if (typeof raw === 'string' && raw.trim() !== '') {
      const parsed = Number(raw)
      if (Number.isFinite(parsed)) return parsed
    }
  }
  return fallbackIndex
}

export const sortByOrder = <T>(items: T[] = []) =>
  [...items]
    .map((item, index) => ({ item, index }))
    .sort(
      (a, b) =>
        resolveCmsEntryOrder(a.item, a.index) - resolveCmsEntryOrder(b.item, b.index) || a.index - b.index
    )
    .map(({ item }) => item)

export const toSlug = (value: string) =>
  value
    .toLowerCase()
    .trim()
    .replace(/&/g, ' and ')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
